import { sum } from 'lodash';
import { getFontFamilyWithFallback, getPlainTextLayoutByPretext } from '@/utils/text-kit';
import { DEFAULT_FONT_FAMILY, type BaseCells, type Cell, type ITableLayer } from '@/version-types';
import {
    DEFAULT_TABLE_CELL__LINE_HEIGHT,
    DEFAULT_TABLE_CELL__TEXT_FONT_SIZE,
    MIN_BASE_CELL_HEIGHT,
    MIN_BASE_CELL_WIDTH,
    TABLE_CELL_HORIZONTAL_PADDING,
} from '../default-configs';
import { getCellStyle } from './cell-style';
import { getTableOuterInsets } from './geometry';

type TableLayerForLayout = ITableLayer | ITableLayer<true>;

/** 表格布局的三类入口；调用方必须明确是修复、交互调整还是按内容适配。 */
export type TableLayoutOptions =
    | {
          mode: 'repair';
          shrinkSpan?: { cellId: string; minimumSpanWidth: number };
      }
    | {
          mode: 'interaction';
          changedAxes: 'horizontal' | 'vertical' | 'both';
      }
    | {
          mode: 'contentFit';
          maxColumnWidth: number;
      };

/** 布局求解后的图层尺寸和基础行列尺寸。 */
interface TableLayoutResult {
    width: number;
    height: number;
    baseCells: BaseCells;
}

/** 固定列宽时，表格可容纳全部内容的最小高度。 */
interface TableMinimumHeightResult {
    height: number;
    verticalSizes: number[];
}

const getRuntimeFontFamilyMap = (layer: TableLayerForLayout): Record<string, string> =>
    '__runtime' in layer ? (layer.__runtime?.fontFamilyMap ?? {}) : {};

const getFontFamily = (layer: TableLayerForLayout, fontFamily: string) => {
    const subsetFontFamily = getRuntimeFontFamilyMap(layer)[fontFamily];
    return getFontFamilyWithFallback(fontFamily, subsetFontFamily);
};

const normalizeSize = (size: number, minimum: number) =>
    Number.isFinite(size) && size > 0 ? Math.max(size, minimum) : minimum;

const getCells = (layer: TableLayerForLayout) => layer.data.cells.flat().filter(Boolean) as Cell[];

/**
 * 校验单元格锚点、合并占位和行列尺寸矩阵的一致性，阻止损坏数据进入布局计算。
 */
export const assertTableLayoutStructure = (layer: TableLayerForLayout) => {
    const { horizontal, vertical } = layer.data.baseCells;
    if (!horizontal.length || !vertical.length) {
        throw new Error('Table layout requires at least one row and one column.');
    }
    if (layer.data.cells.length !== vertical.length) {
        throw new Error('Table row count does not match baseCells.vertical.');
    }
    if (layer.data.cells.some((row) => row.length !== horizontal.length)) {
        throw new Error('Table column count does not match baseCells.horizontal.');
    }

    const coverage = Array.from({ length: vertical.length }, () => Array<Cell | null>(horizontal.length).fill(null));
    layer.data.cells.forEach((row, vIndex) => {
        row.forEach((cell, hIndex) => {
            if (!cell) return;
            if (cell.hIndex !== hIndex || cell.vIndex !== vIndex) {
                throw new Error('Table cell anchor does not match its matrix position.');
            }
            if (
                !Number.isInteger(cell.colSpan) ||
                !Number.isInteger(cell.rowSpan) ||
                cell.colSpan <= 0 ||
                cell.rowSpan <= 0
            ) {
                throw new Error('Table cell span must be a positive integer.');
            }
            if (hIndex + cell.colSpan > horizontal.length || vIndex + cell.rowSpan > vertical.length) {
                throw new Error('Table cell span exceeds the table bounds.');
            }
            for (let rowIndex = vIndex; rowIndex < vIndex + cell.rowSpan; rowIndex++) {
                for (let colIndex = hIndex; colIndex < hIndex + cell.colSpan; colIndex++) {
                    if (coverage[rowIndex][colIndex]) {
                        throw new Error('Table cells overlap.');
                    }
                    if ((rowIndex !== vIndex || colIndex !== hIndex) && layer.data.cells[rowIndex][colIndex] !== null) {
                        throw new Error('A merged table cell must cover null matrix entries.');
                    }
                    coverage[rowIndex][colIndex] = cell as Cell;
                }
            }
        });
    });
    if (coverage.some((row) => row.some((cell) => !cell))) {
        throw new Error('Table cells contain an uncovered matrix entry.');
    }
};

const getTextLayout = (layer: TableLayerForLayout, cell: Cell, contentWidth: number, disableAutoWrap?: boolean) => {
    const style = getCellStyle(cell, layer.data);
    const fontSize = style.fontSize ?? DEFAULT_TABLE_CELL__TEXT_FONT_SIZE;
    return getPlainTextLayoutByPretext(cell.content, {
        fontFamily: getFontFamily(layer, style.fontFamily ?? DEFAULT_FONT_FAMILY().name),
        fontSize,
        bold: style.bold,
        italic: style.italic,
        lineHeightPx: fontSize * DEFAULT_TABLE_CELL__LINE_HEIGHT,
        width: Math.max(1, contentWidth),
        disableAutoWrap: disableAutoWrap ?? style.disableAutoWrap,
        wordBreak: 'break-all',
    });
};

const getCellMinimumContentWidth = (layer: TableLayerForLayout, cell: Cell) => {
    const style = getCellStyle(cell, layer.data);
    if (!cell.content || style.disableAutoWrap) return 0;
    const layout = getTextLayout(layer, cell, 1, false);
    return Math.ceil(Math.max(0, ...layout.lines.map((line) => line.width))) + TABLE_CELL_HORIZONTAL_PADDING * 2;
};

const getCellNaturalContentWidth = (layer: TableLayerForLayout, cell: Cell) => {
    if (!cell.content) return 0;
    const layout = getTextLayout(layer, cell, 1, true);
    return Math.ceil(Math.max(0, ...layout.lines.map((line) => line.width))) + TABLE_CELL_HORIZONTAL_PADDING * 2;
};

export const getTableCellRequiredHeight = (layer: TableLayerForLayout, cell: Cell, horizontalSizes: number[]) => {
    if (!cell.content) return MIN_BASE_CELL_HEIGHT;
    const cellWidth = sum(horizontalSizes.slice(cell.hIndex, cell.hIndex + cell.colSpan));
    const layout = getTextLayout(layer, cell, cellWidth - TABLE_CELL_HORIZONTAL_PADDING * 2);
    return Math.max(MIN_BASE_CELL_HEIGHT, Math.ceil(layout.height));
};

/** 跨行列单元格空间不足时只扩张跨度末端，保持前序分隔线位置稳定。 */
const ensureSpanSize = (sizes: number[], start: number, span: number, requiredSize: number) => {
    const currentSize = sum(sizes.slice(start, start + span));
    const delta = requiredSize - currentSize;
    if (delta > 0) sizes[start + span - 1] += delta;
};

const shrinkSpanSize = (sizes: number[], start: number, span: number, targetSize: number, minimumSize: number) => {
    let rest = Math.max(0, sum(sizes.slice(start, start + span)) - targetSize);
    for (let index = start + span - 1; index >= start && rest > 0; index--) {
        const reducible = Math.max(0, sizes[index] - minimumSize);
        const reduced = Math.min(reducible, rest);
        sizes[index] -= reduced;
        rest -= reduced;
    }
};

const distributeSpanSizeWithCap = (
    sizes: number[],
    start: number,
    span: number,
    requiredSize: number,
    maximumSize: number,
) => {
    let rest = Math.max(0, requiredSize - sum(sizes.slice(start, start + span)));
    let activeIndexes = Array.from({ length: span }, (_, index) => start + index).filter(
        (index) => sizes[index] < maximumSize,
    );
    while (rest > 0.001 && activeIndexes.length) {
        const share = rest / activeIndexes.length;
        let distributed = 0;
        activeIndexes.forEach((index) => {
            const delta = Math.min(share, maximumSize - sizes[index]);
            sizes[index] += delta;
            distributed += delta;
        });
        if (distributed <= 0.001) break;
        rest -= distributed;
        activeIndexes = activeIndexes.filter((index) => sizes[index] < maximumSize - 0.001);
    }
};

const applyMinimumContentWidthConstraints = (layer: TableLayerForLayout, horizontalSizes: number[]) => {
    getCells(layer)
        .sort((a, b) => a.colSpan - b.colSpan || a.vIndex - b.vIndex || a.hIndex - b.hIndex)
        .forEach((cell) => {
            ensureSpanSize(
                horizontalSizes,
                cell.hIndex,
                cell.colSpan,
                Math.max(MIN_BASE_CELL_WIDTH * cell.colSpan, getCellMinimumContentWidth(layer, cell)),
            );
        });
    return horizontalSizes;
};

export const getTableColumnMinimumWidths = (layer: TableLayerForLayout) => {
    assertTableLayoutStructure(layer);
    const horizontalSizes = layer.data.baseCells.horizontal.map(() => MIN_BASE_CELL_WIDTH);
    applyMinimumContentWidthConstraints(layer, horizontalSizes);
    return horizontalSizes;
};

const getContentFitHorizontalSizes = (layer: TableLayerForLayout, maxColumnWidth: number) => {
    const maximumSize = Math.max(MIN_BASE_CELL_WIDTH, maxColumnWidth);
    const horizontalSizes = layer.data.baseCells.horizontal.map(() => MIN_BASE_CELL_WIDTH);
    getCells(layer)
        .sort((a, b) => a.colSpan - b.colSpan || a.vIndex - b.vIndex || a.hIndex - b.hIndex)
        .forEach((cell) => {
            const requiredSize = Math.min(
                maximumSize * cell.colSpan,
                Math.max(MIN_BASE_CELL_WIDTH * cell.colSpan, getCellNaturalContentWidth(layer, cell)),
            );
            distributeSpanSizeWithCap(horizontalSizes, cell.hIndex, cell.colSpan, requiredSize, maximumSize);
        });
    return horizontalSizes;
};

const resolveHorizontalSizes = (layer: TableLayerForLayout, options: TableLayoutOptions) => {
    if (options.mode === 'contentFit') {
        return getContentFitHorizontalSizes(layer, options.maxColumnWidth);
    }

    const horizontalSizes = layer.data.baseCells.horizontal.map((size) => normalizeSize(size, MIN_BASE_CELL_WIDTH));
    if (options.mode === 'repair' && options.shrinkSpan) {
        const cell = getCells(layer).find((item) => item.__renderId === options.shrinkSpan?.cellId);
        if (cell) {
            const targetSize = Math.max(options.shrinkSpan.minimumSpanWidth, getCellMinimumContentWidth(layer, cell));
            shrinkSpanSize(horizontalSizes, cell.hIndex, cell.colSpan, targetSize, MIN_BASE_CELL_WIDTH);
        }
    }

    return applyMinimumContentWidthConstraints(layer, horizontalSizes);
};

const resolveVerticalSizes = (layer: TableLayerForLayout, horizontalSizes: number[], options: TableLayoutOptions) => {
    const verticalSizes =
        options.mode === 'contentFit'
            ? layer.data.baseCells.vertical.map(() => MIN_BASE_CELL_HEIGHT)
            : layer.data.baseCells.vertical.map((size) => normalizeSize(size, MIN_BASE_CELL_HEIGHT));
    getCells(layer)
        .sort((a, b) => a.rowSpan - b.rowSpan || a.hIndex - b.hIndex || a.vIndex - b.vIndex)
        .forEach((cell) => {
            ensureSpanSize(
                verticalSizes,
                cell.vIndex,
                cell.rowSpan,
                Math.max(MIN_BASE_CELL_HEIGHT * cell.rowSpan, getTableCellRequiredHeight(layer, cell, horizontalSizes)),
            );
        });
    return verticalSizes;
};

export const getTableMinimumHeight = (
    layer: TableLayerForLayout,
    horizontalSizes: number[],
): TableMinimumHeightResult => {
    assertTableLayoutStructure(layer);
    if (horizontalSizes.length !== layer.data.baseCells.horizontal.length) {
        throw new Error('Candidate table column count does not match baseCells.horizontal.');
    }
    const verticalSizes = layer.data.baseCells.vertical.map(() => MIN_BASE_CELL_HEIGHT);
    getCells(layer)
        .sort((a, b) => a.rowSpan - b.rowSpan || a.hIndex - b.hIndex || a.vIndex - b.vIndex)
        .forEach((cell) => {
            ensureSpanSize(
                verticalSizes,
                cell.vIndex,
                cell.rowSpan,
                Math.max(MIN_BASE_CELL_HEIGHT * cell.rowSpan, getTableCellRequiredHeight(layer, cell, horizontalSizes)),
            );
        });
    const insets = getTableOuterInsets(layer.data as ITableLayer['data']);
    return {
        height: sum(verticalSizes) + insets.top + insets.bottom,
        verticalSizes,
    };
};

export const getTableRowMinimumSize = (layer: TableLayerForLayout, rowIndex: number) => {
    assertTableLayoutStructure(layer);
    const { horizontal, vertical } = layer.data.baseCells;
    if (rowIndex < 0 || rowIndex >= vertical.length) {
        throw new Error('Table row index is outside baseCells.vertical.');
    }
    return getCells(layer).reduce((minimumSize, cell) => {
        if (cell.vIndex > rowIndex || cell.vIndex + cell.rowSpan <= rowIndex) return minimumSize;
        const requiredHeight = Math.max(
            MIN_BASE_CELL_HEIGHT * cell.rowSpan,
            getTableCellRequiredHeight(layer, cell, horizontal),
        );
        const otherRowsHeight = sum(vertical.slice(cell.vIndex, cell.vIndex + cell.rowSpan)) - vertical[rowIndex];
        return Math.max(minimumSize, requiredHeight - otherRowsHeight);
    }, MIN_BASE_CELL_HEIGHT);
};

export const resolveTableLayout = (
    layer: TableLayerForLayout,
    options: TableLayoutOptions = { mode: 'repair' },
): TableLayoutResult => {
    assertTableLayoutStructure(layer);
    const horizontal = resolveHorizontalSizes(layer, options);
    const vertical = resolveVerticalSizes(layer, horizontal, options);
    const insets = getTableOuterInsets(layer.data as ITableLayer['data']);
    return {
        width: sum(horizontal) + insets.left + insets.right,
        height: sum(vertical) + insets.top + insets.bottom,
        baseCells: { horizontal, vertical },
    };
};

export const applyTableLayout = <T extends TableLayerForLayout>(
    layer: T,
    options: TableLayoutOptions = { mode: 'repair' },
): T => {
    const layout = resolveTableLayout(layer, options);
    layer.width = layout.width;
    layer.height = layout.height;
    layer.data.baseCells = layout.baseCells;
    return layer;
};
